<template lang="pug">
.custom-select-control(ref="master")
  .input-container(ref="container")
    input.input-control(type="text"
      v-model="input"
      @click.prevent="focused = !focused"
      @input="focused = true"
      @focusout="blurIt"
      @keyup.down="moveDown"
      @keyup.up="moveUp"
      @keydown.enter.prevent="enterItem"
      @keyup.esc="focused = false"
      @keydown.home.prevent="moveHome"
      @keydown.end.prevent="moveEnd"
      @keydown.pageup.prevent="movePageUp"
      @keydown.pagedown.prevent="movePageDown"
      autocomplete="off"
      :id="idValue")
    strong.delete-all(@click.prevent="deselectAll") &times;
    img(src="../assets/img/expand.svg",@click.prevent="focused = !focused",:class="{'expanded': focused}")
  .select-area(ref="selectArea",v-show="focused")
    div.option(
      v-for="(option,index) of filteredOptions"
      :key="optionValue(option)"
      @mousedown.prevent="selectItem(option)"
      :class="{'selected-item': selectedItem(option) || index === selectedIndex}"
    ) {{ optionName(option) }}
</template>

<script>
export default {
  name: 'CustomSelect',
  data: function() {
    return {
      input: '',
      singleValue: '',
      multipleValues: [],
      focused: false,
      selectedIndex: -1, // This is the index of the cursor (i.e., what moves up and down with the arrow keys and such).
    }
  },
  props: {
    /**
     * The list of available select options. It can be a plain array, or it can be a list of objects such as `{label: 'Item', value: '1'}`. If
     * you use objects, make sure you also use the labelName and valueName properties to set the keys for the label and option value.
     */
    options: {
      type: Array,
      default: () => [],
    },
    /**
     * Set to true to allow multiple select.
     */
    multiple: {
      type: Boolean,
      default: false,
    },
    /**
     * This prop allows you to set the key for the label to be used in the `options` property.
     */
    labelName: {
      type: String,
      default: '',
    },
    /**
     * This prop allows you to set the key for the value to be used in the `options` property.
     */
    valueName: {
      type: String,
      default: '',
    },
    /**
     * If the value is invalid, apply border color invalidColor below.
     */
    invalid: {
      type: Boolean,
      default: false,
    },
    /**
     * Apply the border color according the invalid property.
     */
    invalidColor: {
      type: String,
      default: '#dc3545',
    },
    value: {
      type: [String, Array],
      default: function() {
        return this.multiple ? [] : ''
      },
    },
    idValue: {
      type: String,
      default: 'custom-select',
    },
  },
  computed: {
    filteredOptions: function() {
      // If there is nothing in the input box, this will return all options, otherwise it returns a filtered set of options.
      // If the current input === the same option as is currently selected, then return the full list of options.
      if (this.input === this.displayName) {
        return this.options
      }
      let options
      if (this.input === '') {
        options = this.options
        if (!this.multiple) this.deselectAll()
      }
      options = this.options.filter(val => {
        let search = this.input.toLowerCase()
        if (this.labelName) {
          return (
            val[this.labelName]
              .toString()
              .toLowerCase()
              .includes(search) ||
            val[this.valueName]
              .toString()
              .toLowerCase()
              .includes(search)
          )
        } else {
          return val.toLowerCase().includes(this.input.toLowerCase())
        }
      })
      return options
    },
    displayName: function() {
      if (this.multiple) {
        return this.multipleValues
          .map(val => this.optionByValue(val))
          .map(val => this.optionName(val))
          .join(', ')
      } else if (!this.multiple && this.singleValue !== '') {
        return this.optionName(this.optionByValue(this.singleValue))
      } else {
        return ''
      }
    },
  },
  watch: {
    invalid: function(val) {
      val
        ? (this.$refs.container.style.borderColor = this.invalidColor)
        : (this.$refs.container.style.borderColor = 'rgb(206, 212, 218)')
    },
    value: function(val) {
      if (val === [] || val === '') {
        this.deselectAll()
      } else if (this.multiple) {
        this.multipleValues = val
      } else if (!this.multiple) {
        this.singleValue = val
      }
    },
    singleValue: function() {
      this.input = this.displayName
    },
    multipleValues: function() {
      this.input = this.displayName
    },
    input(val) {
      this.$emit('filter', val)
    },
  },
  mounted: function() {
    if (this.multiple) {
      this.multipleValues = this.value
    } else {
      this.singleValue = this.value
    }
  },
  methods: {
    blurIt: function(event) {
      // IE11 registers a blur event when you scroll, so we need to check if the relatedTarget (the clicked element in a blur even)
      // is a child of the master node. If it is, that means the blur even can be safely ignore (such as clicking on a scrollbar for
      // a child node).
      if (!this.$refs.master.contains(event.relatedTarget)) this.focused = false
    },
    selectItem: function(item) {
      // If this is a multiple select field, it will toggle the item the user selects in the multipleValues array.
      // If it's a single select, it will set the value. Either way, it will emit the event to alert the parent compontents.
      if (this.multiple) {
        let idx = this.multipleValues.indexOf(this.optionValue(item))
        if (idx > -1) {
          this.multipleValues.splice(idx, 1)
        } else {
          this.multipleValues.push(this.optionValue(item))
        }
      } else {
        this.singleValue = this.optionValue(item)
        this.focused = false
        if (!this.multiple) this.input = this.displayName
      }
      this.emitEvent()
      this.selectedIndex = -1
    },
    selectedItem: function(item) {
      // Checks an item to see if it is the currently selected item. Return true or false.
      if (this.multiple) {
        return this.multipleValues.indexOf(this.optionValue(item)) > -1
      } else {
        return this.singleValue === this.optionValue(item)
      }
    },
    optionName: function(option) {
      // Returns the current name (label) of the provided option.
      return (option && option[this.labelName]) || option
    },
    optionValue: function(option) {
      // Returns the current value of the provided option.
      return option[this.valueName] || option
    },
    optionByValue: function(value) {
      return this.options.filter(opt => this.optionValue(opt) === value)[0]
    },
    optionByLabel: function(value) {
      return this.options.filter(opt => this.optionName(opt) === value)[0]
    },
    deselectAll: function() {
      // Deselect all items.
      this.input = ''
      this.singleValue = ''
      this.multipleValues = []
      this.selectedIndex = -1
      this.emitEvent()
    },
    emitEvent() {
      // Send the event when the select values are changed.
      this.multiple
        ? this.$emit(
            'input',
            this.multipleValues.map(val => this.optionValue(val)),
          )
        : this.$emit('input', this.singleValue)
    },
    moveDown: function() {
      // Move the cursor down.
      this.focused = true
      this.selectedIndex = Math.min(
        this.selectedIndex + 1,
        this.filteredOptions.length - 1,
      )
      this.scroll('down')
    },
    moveUp: function() {
      // Move the cursor up.
      this.focused = true
      this.selectedIndex = Math.max(this.selectedIndex - 1, 0)
      this.scroll('up')
    },
    enterItem() {
      // Pressed enter on the item.
      this.selectItem(this.filteredOptions[this.selectedIndex])
    },
    moveHome: function() {
      // Go to top
      this.focused = true
      this.selectedIndex = 0
      this.scroll('up')
    },
    moveEnd: function() {
      // Go to bottom
      this.focused = true
      this.selectedIndex = this.filteredOptions.length - 1
      this.scroll('down')
    },
    movePageUp: function() {
      // Calculate the number of items that can fit on one page, then scrolls down that many items accordingly
      this.focused = true
      let itemsPerPage = parseInt(
        this.$refs.selectArea.offsetHeight /
          document.querySelector('.option').offsetHeight,
      )
      this.selectedIndex = Math.max(this.selectedIndex - itemsPerPage, 0)
      this.scroll('up')
    },
    movePageDown: function() {
      // Calculate the number of items that can fit on one page, then scrolls up that many items accordingly
      this.focused = true
      let itemsPerPage = parseInt(
        this.$refs.selectArea.offsetHeight /
          document.querySelector('.option').offsetHeight,
      )
      this.selectedIndex = Math.min(
        this.selectedIndex + itemsPerPage,
        this.filteredOptions.length - 1,
      )
      this.scroll('down')
    },
    scroll: function(dir) {
      // Scroll up or down to the current index. The DOM makes this stupid and difficult. Change your own peril!
      if (this.selectedIndex < 0) {
        return
      }
      let elem = document.querySelectorAll('.option')[this.selectedIndex]
      let selectArea = this.$refs.selectArea
      let elemBottom = elem.offsetTop + elem.offsetHeight
      if (
        dir === 'down' &&
        elemBottom > selectArea.scrollTop + selectArea.offsetHeight
      ) {
        selectArea.scrollTop =
          elem.offsetTop - selectArea.offsetHeight + elem.offsetHeight
      }
      if (dir === 'up' && elem.offsetTop < selectArea.scrollTop) {
        selectArea.scrollTop = elem.offsetTop
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../assets/styles/custom';
.select-area {
  position: absolute;
  max-height: 200px;
  overflow-y: scroll;
  background: white;
  width: inherit;
  right: 5px;
  left: 5px;
  border: 1px solid rgb(206, 212, 218);
  overflow-x: hidden;
  box-shadow: 0 2px 5px rgba(black, 0.3);
  z-index: 1000;
}

.selected-item {
  background-color: $primary;
  color: white;
}

.option:hover {
  @extend .selected-item;
}

.option {
  padding: 3px;
}

.input-container {
  border: 1px solid rgb(206, 212, 218);
  display: flex;
  background: white;

  .input-control {
    border: 0;
    outline: 0;
    box-shadow: none;
    flex-grow: 1;
    padding: 6px 12px;
    line-height: 1.5rem;
  }

  img {
    width: 0.8rem;
    height: 0.8rem;
    margin-top: auto;
    margin-bottom: auto;
    margin-right: 3px;
    opacity: 0.4;
    transition: transform 0.2s;
  }

  img.expanded {
    transform: rotate(180deg);
    transition: transform 0.2s;
  }

  img:hover {
    opacity: 1;
  }

  .delete-all {
    font-size: 1.2rem;
    font-weight: bold;
    margin-top: auto;
    margin-bottom: auto;
    margin-right: 3px;
    opacity: 0.4;
  }

  .delete-all:hover {
    opacity: 1;
    cursor: pointer;
  }

  // Vendor prefix to remove Edge/IE11's 'x' on input fields
  input::-ms-clear {
    display: none;
  }
}
</style>
